import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { css, Theme, useTheme } from '@emotion/react';
import { lighten, transparentize } from 'polished';
import type { AvatarProps } from 'client/components/Other/Avatar';
import { ListItemFigure } from 'client/components/Other/ListItemFigure';
import { Typography } from 'client/components/Typography';

export interface BreadcrumbsItemProps {
  title: string | JSX.Element;
  icon?: JSX.Element | null;
  image?: AvatarProps | null;
  href?: React.ComponentProps<typeof Link>['to'] | null;
  disabled?: boolean;
}

export const BreadcrumbsItem = ({ title, icon: iconProp = null, image = null, href, disabled = false, ...other }: BreadcrumbsItemProps) => {
  const theme = useTheme();
  const icon = useMemo(
    () => (iconProp ? <iconProp.type size={18} weight="fill" color={lighten(0.1, theme.color.grey[100])} {...iconProp.props} /> : iconProp),
    [iconProp]
  );

  const content = (
    <Typography component="li" variant="caption" css={[breadcrumbStyle, disabled && disabledStyle]} {...other}>
      {(icon || image) && <ListItemFigure icon={icon} image={image} className="icon" size={18} />}
      {title}
    </Typography>
  );

  if (href) {
    return (
      <Link to={href} css={linkStyle}>
        {content}
      </Link>
    );
  }

  return content;
};

const breadcrumbStyle = (theme: Theme) => css`
  display: flex;
  align-items: center;
  list-style: none;
  user-select: none;
  color: ${transparentize(0.1, theme.color.foreground)};

  > a {
    display: flex;
    align-items: center;
  }

  > .icon {
    margin-right: 4px;
    width: 18px;
    height: 18px;
  }
`;

const linkStyle = (theme: Theme) => css`
  position: relative;
  text-decoration: none;
  color: inherit;

  // Make the click target larger
  &::after {
    content: '';
    display: block;
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    transform: scale(1.25);
  }

  &:hover > li {
    color: ${theme.color.primary[100]};
  }
`;

const disabledStyle = (theme: Theme) => css`
  color: ${transparentize(0.4, theme.color.foreground)};
`;
